import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { TextField } from "@mui/material";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { resetPassword, resendOtp } from "../../requests/auth/Authentication";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

// * Request Validation
const validationSchema = Yup.object({
  resetCode: Yup.string()
    .required("Reset code is required")
    .matches(/^[0-9]{4}$/, "Must be exactly 4 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
});

const initialValues = {
  resetCode: "",
  password: "",
};

const getPasswordStrength = (password) => {
  if (!password) return '';
  if (password.length < 8) return 'Weak';
  if (password.length < 12) return 'Medium';
  if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) return 'Strong';
  return 'Good';
};

const getPasswordStrengthColor = (password) => {
  if (!password) return '';
  if (password.length < 8) return 'bg-red-400';
  if (password.length < 12) return 'bg-yellow-400';
  if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) return 'bg-green-400';
  return 'bg-blue-400';
};

const getPasswordStrengthWidth = (password) => {
  if (!password) return '0%';
  if (password.length < 8) return '25%';
  if (password.length < 12) return '50%';
  if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) return '100%';
  return '75%';
};

function PasswordReset() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const vendor_slug = location.state?.vendorSlug || searchParams.get("vendorSlug");
  const email = location.state?.email || searchParams.get("email");

  const resetPasswordMutation = useMutation(
    resetPassword,
    {
      onSuccess: (data) => {
        const response = data?.data;
        
        if (response?.in_error === true) {
          let errorMessage = response?.reason || "Password reset failed";
          
          switch (response?.status_code) {
            case "004":
              if (response?.reason === "Otp not found") {
                errorMessage = "The verification code has expired or is invalid. Please request a new code.";
              }
              break;
            default:
              errorMessage = response?.reason || "Something went wrong with the password reset";
          }
          toast.error(errorMessage);
          return;
        }

        // Success case
        toast.success("Password reset successfully!");
        navigate("/signin");
      },
      onError: (error) => {
        // Handle network or unexpected errors
        const errorMessage = error?.response?.data?.reason 
          || error?.response?.data?.message 
          || "Unable to reset password. Please try again later.";
        
        toast.error(errorMessage);        
        if (error?.response?.data?.status_code === "004") {
          setTimeout(() => {
            navigate("/forgot-password");
          }, 2000);
        }
      },
    }
  );

  const resendOtpMutation = useMutation(
    resendOtp,
    {
      onSuccess: (data) => {
        const response = data?.data;
        
        if (response?.in_error === true) {
          toast.error(response?.reason || "Failed to resend code");
          return;
        }

        toast.success("Verification code resent successfully!");
        setResendTimer(30); // Start 30 second countdown
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.reason 
          || error?.response?.data?.message 
          || "Unable to resend code. Please try again later.";
        toast.error(errorMessage);
      }
    }
  );

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  const handleResendClick = async () => {
    if (resendTimer > 0) return;
    setIsResending(true);
    try {
      await resendOtpMutation.mutateAsync({ email:email });
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = (values) => {
    resetPasswordMutation.mutate({
      password: values.password,
      vendor_slug: vendor_slug,
      otp: values.resetCode
    });
  };

  useEffect(() => {
    if (!vendor_slug) {
      toast.error("Invalid reset password link");
      navigate("/signin");
    }
  }, [vendor_slug, navigate]);

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Animated Background Elements */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Vendor Security</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Business Protection</span>
          </div>
        </motion.div>

        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_first}
              alt="reset-password"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
            />
            
            {/* Content Overlay */}
            <motion.div 
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div className="px-8 text-center">
                <motion.h2 
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Reset Password
                </motion.h2>
                <motion.p 
                  className="mx-auto max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Create a new secure password for your account
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div 
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
        </motion.div>

        {/* Back Button */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#808080] hover:text-[#FF6C6C] transition-colors duration-300"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="w-5 h-5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 19l-7-7 7-7" 
              />
            </svg>
            <span className="font-medium">Back</span>
          </motion.button>
        </motion.div>

        {/* Title and Description */}
        <motion.div
          className="mb-8 space-y-3"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Reset Your Password
          </h1>
          <p className="text-[#808080] text-base">
            Enter the 4-digit code sent to your email and create a new password
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, values }) => (
            <Form className="space-y-8" onSubmit={handleSubmit}>
              {/* Reset Code Input */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <Field name="resetCode">
                  {({ field, form }) => (
                    <div className="flex gap-4 justify-center">
                      {[...Array(4)].map((_, index) => (
                        <motion.input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength="1"
                          className="w-16 h-16 text-center text-2xl font-bold bg-white border-2 rounded-2xl focus:border-[#FF6C6C] focus:ring-4 focus:ring-[#FF6C6C]/20 focus:outline-none transition-all duration-200 shadow-sm"
                          value={field.value[index] || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            if (value.length <= 1) {
                              const newValue = field.value.split('');
                              newValue[index] = value;
                              form.setFieldValue('resetCode', newValue.join(''));
                              
                              if (value && e.target.nextElementSibling) {
                                e.target.nextElementSibling.focus();
                              }
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !field.value[index] && index > 0) {
                              e.target.previousElementSibling?.focus();
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          whileFocus={{ scale: 1.05 }}
                        />
                      ))}
                    </div>
                  )}
                </Field>
                <ErrorMessage 
                  name="resetCode" 
                  component="div" 
                  className="text-sm text-center text-red-500" 
                />
              </div>

              <motion.div 
                className="flex justify-end -mt-4 mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.button
                  type="button"
                  onClick={handleResendClick}
                  disabled={isResending || resendTimer > 0}
                  className={`text-sm ${
                    resendTimer > 0 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-[#FF6C6C] hover:text-[#ff8b8b]'
                  } font-medium transition-colors duration-300`}
                  whileHover={{ scale: isResending || resendTimer > 0 ? 1 : 1.05 }}
                  whileTap={{ scale: isResending || resendTimer > 0 ? 1 : 0.95 }}
                >
                  {isResending ? (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="inline-flex gap-1 items-center"
                    >
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="inline-block"
                      >
                        ⟳
                      </motion.span>
                      Resending...
                    </motion.span>
                  ) : resendTimer > 0 ? (
                    `Resend in ${resendTimer}s`
                  ) : (
                    "Resend Code"
                  )}
                </motion.button>
              </motion.div>

              {/* Password Input */}
              <div className="space-y-4">
                <Field name="password">
                  {({ field }) => (
                    <div className="space-y-2">
                      <TextField
                        {...field}
                        type={showPassword ? "text" : "password"}
                        label="New Password"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="cursor-pointer text-gray-500 hover:text-[#FF6C6C]"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-5 h-5"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-5 h-5"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              )}
                            </motion.div>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            backgroundColor: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            },
                            '&.Mui-focused': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                              '& fieldset': {
                                borderColor: '#FF6C6C',
                                borderWidth: '2px',
                              }
                            }
                          },
                          '& .MuiInputLabel-root': {
                            color: '#808080',
                            '&.Mui-focused': {
                              color: '#FF6C6C'
                            }
                          }
                        }}
                      />
                      
                      {/* Password Requirements Indicators */}
                      <div className="mt-3 space-y-2">
                        <div className="mb-2 text-sm text-gray-600">Password requirements:</div>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            {
                              label: "8+ Characters",
                              valid: field.value.length >= 8,
                              icon: "📏"
                            },
                            {
                              label: "Uppercase Letter",
                              valid: /[A-Z]/.test(field.value),
                              icon: "🔠"
                            },
                            {
                              label: "Lowercase Letter",
                              valid: /[a-z]/.test(field.value),
                              icon: "🔡"
                            },
                            {
                              label: "Number",
                              valid: /\d/.test(field.value),
                              icon: "🔢"
                            },
                            {
                              label: "Special Character",
                              valid: /[@$!%*?&]/.test(field.value),
                              icon: "💫"
                            }
                          ].map((requirement, index) => (
                            <motion.div
                              key={index}
                              className={`flex items-center gap-2 text-sm ${
                                requirement.valid ? 'text-green-600' : 'text-gray-400'
                              }`}
                              animate={{
                                opacity: field.value ? 1 : 0.7,
                                scale: requirement.valid ? 1 : 0.98
                              }}
                            >
                              <span className="text-base">{requirement.icon}</span>
                              <span>{requirement.label}</span>
                              {requirement.valid && (
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="text-green-500"
                                >
                                  ✓
                                </motion.span>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Password Strength Bar */}
                      {field.value && (
                        <motion.div 
                          className="mt-4"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-gray-600">Password Strength</span>
                            <span className="text-sm font-medium">
                              {getPasswordStrength(field.value)}
                            </span>
                          </div>
                          <div className="overflow-hidden h-1.5 bg-gray-100 rounded-full">
                            <motion.div
                              className={`h-full transition-all duration-500 ${
                                getPasswordStrengthColor(field.value)
                              }`}
                              initial={{ width: '0%' }}
                              animate={{ width: getPasswordStrengthWidth(field.value) }}
                            />
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}
                </Field>
                <ErrorMessage 
                  name="password" 
                  component="div" 
                  className="text-sm text-red-500" 
                />
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={resetPasswordMutation.isLoading}
              >
                {resetPasswordMutation.isLoading ? (
                  <div className="flex gap-2 justify-center items-center">
                    <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    <span>Resetting Password...</span>
                  </div>
                ) : (
                  "Reset Password"
                )}
              </motion.button>
            </Form>
          )}
        </Formik>

        {/* Security Features */}
        <motion.div
          className="grid grid-cols-3 gap-4 mt-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {[
            { icon: "🔒", text: "Secure Reset" },
            { icon: "🛡️", text: "Protected" },
            { icon: "✨", text: "Easy Process" }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="p-4 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md"
              whileHover={{ y: -5 }}
            >
              <div className="mb-2 text-2xl">{item.icon}</div>
              <div className="text-sm text-gray-600">{item.text}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}

export default PasswordReset;
